import { tenantApi, GoogleReview, ManualReviewRequest, Asset } from "../lib/tenant-api";
import { PageLoader } from '../components/common';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Database, Loader2, Plus, Edit2, Trash2, Star, X, Image } from "lucide-react";
import { useState } from "react";


export default function ReviewManagement() {
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingReview, setEditingReview] = useState<GoogleReview | null>(null);
    const [selectedAssetId, setSelectedAssetId] = useState<string>('');
    const [formData, setFormData] = useState<ManualReviewRequest>({
        author: '',
        rating: '5',
        comment: '',
        review_uri: '',
        asset_id: ''
    });

    const queryClient = useQueryClient();

    const { data: reviews, isLoading: reviewLoading, error: reviewError } = useQuery({
        queryKey: ['reviews'],
        queryFn: () => tenantApi.listReviews(),
        keepPreviousData: true,
    });

    const { data: assets } = useQuery({
        queryKey: ['assets'],
        queryFn: () => tenantApi.getAssetsWithTags(undefined, 'image'),
        keepPreviousData: true,
    });

    const createMutation = useMutation({
        mutationFn: (data: ManualReviewRequest) => tenantApi.createManualReview(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['reviews'] });
            resetForm();
        }
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: ManualReviewRequest }) =>
            tenantApi.updateManualReview(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['reviews'] });
            resetForm();
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => tenantApi.deleteManualReview(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['reviews'] });
        }
    });

    const resetForm = () => {
        setFormData({
            author: '',
            rating: '5',
            comment: '',
            review_uri: '',
            asset_id: ''
        });
        setSelectedAssetId('');
        setShowAddModal(false);
        setEditingReview(null);
    };

    const handleEdit = (review: GoogleReview) => {
        setEditingReview(review);

        // Find asset ID from author photo URL if it exists
        let assetId = '';
        if (review.author_photo_url && assets) {
            const matchingAsset = assets.find(asset =>
                asset.custom_metadata.public_url === review.author_photo_url
            );
            if (matchingAsset) {
                assetId = matchingAsset.id;
            }
        }

        setFormData({
            author: review.author || '',
            rating: review.rating || '5',
            comment: review.comment || '',
            review_uri: review.review_uri || '',
            asset_id: assetId
        });
        setSelectedAssetId(assetId);
        setShowAddModal(true);
    };

    const handleAssetSelect = (assetId: string) => {
        setSelectedAssetId(assetId);
        setFormData({ ...formData, asset_id: assetId });
    };

    const handleDelete = (reviewId: string) => {
        if (confirm('Are you sure you want to delete this manual review?')) {
            deleteMutation.mutate(reviewId);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (editingReview) {
            updateMutation.mutate({ id: editingReview.id, data: formData });
        } else {
            createMutation.mutate(formData);
        }
    };

    const renderStars = (rating: string | number) => {
        const numRating = typeof rating === 'string' ? parseInt(rating) || 0 : rating;
        return (
            <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                        key={star}
                        className={`h-4 w-4 ${star <= numRating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                            }`}
                    />
                ))}
            </div>
        );
    };

    return (
        <div>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Business Reviews</h1>
                        <p className="mt-1 text-sm text-gray-500">
                            Manage your business reviews from Google and manual entries
                        </p>
                    </div>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="inline-flex justify-center items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Manual Review
                    </button>
                </div>
                <div className="bg-white shadow ">
                    <div className="px-4 py-5 sm:p-6">


                        {reviewLoading ? (
                            <PageLoader />
                        ) : reviewError ? (
                            <div className="bg-red-50 border border-red-200  p-4">
                                <div className="flex">
                                    <AlertCircle className="h-5 w-5 text-red-400" />
                                    <div className="ml-3">
                                        <h3 className="text-sm font-medium text-red-800">Error loading reviews</h3>
                                        <p className="mt-1 text-sm text-red-700">
                                            Failed to load google reviews. Please try refreshing.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : reviews && reviews.length > 0 ? (
                            <>
                                <div className="space-y-4">
                                    {reviews.map((review: GoogleReview) => (
                                        <div key={review.id} className={`border  p-4 hover:bg-gray-50 ${review.source === 'manual' ? 'border-green-200 bg-green-50/50' : 'border-indigo-200 bg-indigo-50/50'
                                            }`}>
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-start space-x-3 flex-1">
                                                    {/* Author photo */}
                                                    {review.author_photo_url ? (
                                                        <img
                                                            src={review.author_photo_url}
                                                            alt={review.author}
                                                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                                        />
                                                    ) : (
                                                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                                            <span className="text-gray-500 text-sm font-medium">
                                                                {review.author?.charAt(0).toUpperCase() || '?'}
                                                            </span>
                                                        </div>
                                                    )}

                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center space-x-3 mb-2">
                                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${review.source === 'manual'
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-indigo-100 text-indigo-800'
                                                                }`}>
                                                                {review.source === 'manual' ? 'Manual' : 'Google'}
                                                            </span>
                                                            {renderStars(review.rating)}
                                                        </div>

                                                        <p className="text-sm text-gray-900 mb-2 line-clamp-3">
                                                            {review.comment}
                                                        </p>

                                                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                                                            <span>Author: {review.author}</span>
                                                            <span>Published: {new Date(review.publish_time).toLocaleDateString()}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Action buttons for manual reviews only */}
                                                {review.source === 'manual' && (
                                                    <div className="flex items-center space-x-2 ml-4">
                                                        <button
                                                            onClick={() => handleEdit(review)}
                                                            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 "
                                                            title="Edit review"
                                                        >
                                                            <Edit2 className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(review.id)}
                                                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 "
                                                            title="Delete review"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="text-center py-8">
                                <Database className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-2 text-sm font-medium text-gray-900">No review found</h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    No review have been fetched yet or synced from Google.
                                </p>
                            </div>
                        )}
                    </div>
                </div>


            </div>

            {/* Modal for Add/Edit Manual Review */}
            {showAddModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 px-2 sm:px-4">
                    <div className="relative top-8 sm:top-20 mx-auto p-4 sm:p-5 border shadow-lg max-w-2xl w-full bg-white max-h-[95vh] overflow-y-auto mb-8">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-900">
                                {editingReview ? 'Edit Manual Review' : 'Add Manual Review'}
                            </h3>
                            <button
                                onClick={resetForm}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Author Name *
                                </label>
                                <input
                                    type="text"
                                    required
                                    value={formData.author}
                                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300  focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="Enter reviewer name"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Rating *
                                </label>
                                <select
                                    value={formData.rating}
                                    onChange={(e) => setFormData({ ...formData, rating: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300  focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                >
                                    <option value="5">5 Stars - Excellent</option>
                                    <option value="4">4 Stars - Very Good</option>
                                    <option value="3">3 Stars - Good</option>
                                    <option value="2">2 Stars - Fair</option>
                                    <option value="1">1 Star - Poor</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Review Comment *
                                </label>
                                <textarea
                                    required
                                    rows={4}
                                    value={formData.comment}
                                    onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300  focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="Enter the review comment..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Author Photo (Optional)
                                </label>
                                <div className="space-y-3">
                                    {assets && assets.length > 0 ? (
                                        <div className="grid grid-cols-4 gap-2 max-h-32 overflow-y-auto">
                                            <div
                                                onClick={() => handleAssetSelect('')}
                                                className={`border-2  p-2 cursor-pointer flex items-center justify-center h-16 ${selectedAssetId === '' ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                                                    }`}
                                            >
                                                <span className="text-xs text-gray-500 text-center">No Photo</span>
                                            </div>
                                            {assets.map((asset: Asset) => (
                                                <div
                                                    key={asset.id}
                                                    onClick={() => handleAssetSelect(asset.id)}
                                                    className={`border-2  p-1 cursor-pointer ${selectedAssetId === asset.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                                                        }`}
                                                >
                                                    <img
                                                        src={asset.custom_metadata.public_url}
                                                        alt={asset.file_name}
                                                        className="w-full h-12 object-cover "
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-4 text-sm text-gray-500">
                                            <Image className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                                            No image assets available. Upload images in Assets to use as author photos.
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Review URL (Optional)
                                </label>
                                <input
                                    type="url"
                                    value={formData.review_uri}
                                    onChange={(e) => setFormData({ ...formData, review_uri: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300  focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="https://..."
                                />
                            </div>

                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200  focus:outline-none focus:ring-2 focus:ring-gray-500"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={createMutation.isPending || updateMutation.isPending}
                                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700  focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                                >
                                    {createMutation.isPending || updateMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        editingReview ? 'Update Review' : 'Add Review'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}
